import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef} from "react";
import Utils, {formatMoney} from "../../../../../../../../core/services/utils/utils";
import Button from "../../../../../../../components/base/button/button";
import {ArrowForward, EditOutlined, KeyboardArrowRight} from "@mui/icons-material";
import {Fade, SvgIcon, TextField} from "@mui/material";
import IconButton from "../../../../../../../components/base/button/icon_button";
import SaveOrRollback from "../../../../../../../components/app-specific/save-or-rollback";
import classnames from "classnames";
import {ReactComponent as SaveIcon} from "../../../../../../../../assets/images/save-rollback/check.svg";
import {ShippingFeesDataGridColumnNames} from "../../../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../packages/data-grid";
import {ReactComponent as Passed} from "../../../../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../../../../assets/images/validations/failed.svg";
import PartnerDataGrid from "../../../../../../../containers/data-grid";
import DateUtils from "../../../../../../../../core/services/date-utils";
import {Link} from "react-router-dom";
import {DatePicker} from "@mui/x-date-pickers";

/**
 * The form name identifier for actual cost input.
 * @param id
 * @returns {`actual-cost-${string}`}
 */
const actualCostIdentifier = (id) => `actual-cost-${id}`;

/**
 * The form name identifier for actual date input.
 * @param id
 * @returns {`actual-delivery-date-${string}`}
 */
const actualDateIdentifier = (id) => `actual-delivery-date-${id}`;

const inputTypes = {
    cost: 'cost',
    date: 'date',
}

const ShippingFeesListViewTable = ({
                                       excludeColumns = [],
                                       feeEditableAfterUpdate = true,
                                       dateEditableAfterUpdate = true,
                                       data,
                                       updateActualFee,
                                       updateActualDate,
                                       setShippingFeeProps,
                                       searchData,
                                       dataGridSaveConfig,
                                       exportToCsv,
                                       getInvoiceLink,
                                   }) => {
    const actualFeeRefs = useRef({});
    const actualDateRefs = useRef({});
    /**
     * Copies the estimation fee of the given shipping fee to the actual cost input of the same shipping fee row.
     * @type {(shippingFee: any, type: string) => void}
     */
    const copyEstimationToActualValues = useCallback((shippingFee, type) => {
        switch (type) {
            case inputTypes.cost: {
                const name = actualCostIdentifier(shippingFee.id);
                actualFeeRefs.current[name]?.copyValue(shippingFee.estimatedFee?.toString() ?? '');
                break;
            }
            case inputTypes.date: {
                const name = actualDateIdentifier(shippingFee.id);
                actualDateRefs.current[name]?.copyValue(shippingFee.estimatedDeliveryDateTime ? DateUtils.serverToUTC(shippingFee.estimatedDeliveryDateTime) : undefined);
                break;
            }
            default:
                return;
        }
    }, [])

    /**
     * Sets the property of in-edit-mode for the given shipping fee.
     * @type {(shippingFee: any, value: boolean, type: string) => void}
     */
    const setInputInEditMode = useCallback((shippingFee, value, type) => {
        switch (type) {
            case inputTypes.cost: {
                setShippingFeeProps(shippingFee.id, {actualCostInEditMode: value});
                break;
            }
            case inputTypes.date: {
                setShippingFeeProps(shippingFee.id, {actualDateInEditMode: value});
                break;
            }
            default:
                return;
        }
    }, [setShippingFeeProps])

    /**
     * Confirms the changes of the input and calls appropriate callbacks.
     * @type {(shippingFee: any, value: any, type: string) => void}
     */
    const confirmInputChanges = useCallback((shippingFee, value, type) => {
        switch (type) {
            case inputTypes.cost: {
                const cost = !value?.length ? null : Number(value);
                if (shippingFee.actualFee === cost) {
                    return setInputInEditMode(shippingFee, false, type);
                }
                updateActualFee(shippingFee, cost);
                break;
            }
            case inputTypes.date: {
                const date = !!value ? value : null;
                if ((shippingFee.actualDelivery === null && date === null) || (DateUtils.serverToUTC(shippingFee.actualDelivery).isSame(value, 'day'))) {
                    return setInputInEditMode(shippingFee, false, type);
                }
                updateActualDate(shippingFee, date ? DateUtils.UTCToServer(date).format(DateUtils.withoutOffsetFormat) : null);
                break;
            }
            default:
                return;
        }
    }, [setInputInEditMode, updateActualDate, updateActualFee])

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    name: ShippingFeesDataGridColumnNames.invoiceNo,
                    title: 'Invoice No',
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    pinned: true,
                },
                {
                    name: ShippingFeesDataGridColumnNames.estimatedDeliveryDate,
                    title: 'Estimated Delivery Date',
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.element,
                    width: 180,
                },
                {
                    name: ShippingFeesDataGridColumnNames.actualDeliveryDate,
                    title: 'Actual Delivery Date',
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.element,
                    width: 200,
                },
                {
                    name: ShippingFeesDataGridColumnNames.estimatedFee,
                    title: 'Freight Quote',
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.element,
                    width: 120,
                },
                {
                    name: ShippingFeesDataGridColumnNames.actualFee,
                    title: 'Actual Fee',
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.element,
                    width: 150,
                },
                {
                    name: ShippingFeesDataGridColumnNames.customerFee,
                    title: 'Customer Charges',
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.string,
                    width: 120,
                },
                {
                    name: ShippingFeesDataGridColumnNames.customer,
                    title: 'Customer',
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: 150,
                },
                {
                    name: ShippingFeesDataGridColumnNames.freightCompany,
                    title: 'Freight Company',
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: 150,
                    sortable: false,
                },
                {
                    name: ShippingFeesDataGridColumnNames.eligibleForFreeShipping,
                    title: 'Free Eligibility',
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    width: 120,
                },
                {
                    name: ShippingFeesDataGridColumnNames.reason,
                    title: 'Friendly Reason',
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    width: 200,
                    sortable: false,
                },
                {
                    name: ShippingFeesDataGridColumnNames.submittedBy,
                    title: 'Submitted By',
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    name: ShippingFeesDataGridColumnNames.submittedDate,
                    title: 'Submitted Date',
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.dateTime,
                    width: 120,
                },
                {
                    name: ShippingFeesDataGridColumnNames.confirmedBy,
                    alignment: DataGridColumnAlignments.left,
                    title: 'Confirmed By',
                    type: DataGridColumnTypes.string,
                    width: 110,
                    sortable: false,
                }
            ]
                .filter(e => !excludeColumns.includes(e.name)),
        [excludeColumns])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /**
     * Creates the estimation and actual columns based on the provided type.
     *
     * @param type - The type of the input.
     * @param shippingFee - The shipping fee.
     * @type {(type: string, shippingFee: any) => Record<string, any>}
     */
    const createEstimationAndActualColumns = useCallback((type, shippingFee) => {
        switch (type) {
            case inputTypes.cost: {
                const actualFeeFormName = actualCostIdentifier(shippingFee.id);
                const confirmingActualFee = shippingFee?.confirmingActualCost;
                const actualFeeInEditMode = shippingFee?.actualCostInEditMode;
                const actualFeeConfirmed = shippingFee?.actualCostConfirmed;
                const showConfirmedStateForActualFee = shippingFee?.actualCostConfirmedLocally && !feeEditableAfterUpdate;

                const showInput = (!(shippingFee?.actualFee?.toString()) || actualFeeInEditMode);
                const showCopyEstimationToActualValuesButton = showInput && !confirmingActualFee;

                return {
                    [ShippingFeesDataGridColumnNames.estimatedFee]:
                        <div className={'d-flex align-items-center justify-content-end'}>
                            <p className={'text-sm'}>
                                {formatMoney(shippingFee?.estimatedFee)}
                            </p>
                            <Fade in={showCopyEstimationToActualValuesButton}>
                                <IconButton
                                    disabled={!showCopyEstimationToActualValuesButton}
                                    color={'secondary'}
                                    onPressed={() => copyEstimationToActualValues(shippingFee,
                                        // form,
                                        type)}
                                    className={'ml-2'}
                                >
                                    <ArrowForward className='icon normal'/>
                                </IconButton>
                            </Fade>
                        </div>,
                    [ShippingFeesDataGridColumnNames.actualFee]: showConfirmedStateForActualFee
                        ? <>
                            <p className={'mr-2'}>
                                {formatMoney(shippingFee?.actualFee)}
                            </p>
                            <div className={classnames('confirmation-container confirmed')}>
                                <SaveIcon className={classnames(`save`, "mr-1")}/>
                                <p>
                                    Confirmed
                                </p>
                            </div>
                        </>
                        : !showInput
                            ? <div className={'d-flex align-items-center justify-content-end'}>
                                <p className={'text-sm'}>
                                    {formatMoney(shippingFee?.actualFee)}
                                </p>
                                <IconButton
                                    onPressed={() => setInputInEditMode(shippingFee, true, type)}
                                    className={'ml-2'}
                                    color={'secondary'}
                                >
                                    <SvgIcon className='icon normal' component={EditOutlined}/>
                                </IconButton>
                            </div>
                            : <div className={'d-flex align-items-center justify-content-end'}>
                                <FeeInputSection
                                    value={shippingFee?.actualFee?.toString() ?? ''}
                                    disabled={confirmingActualFee}
                                    actualFeeConfirmed={actualFeeConfirmed}
                                    save={(value) => confirmInputChanges(shippingFee, value, type)}
                                    resetRequested={() => setInputInEditMode(shippingFee, false, type)}
                                    ref={ref => {
                                        actualFeeRefs.current[actualFeeFormName] = ref;
                                    }}
                                />
                            </div>,
                }
            }
            case inputTypes.date: {
                const actualDateFormName = actualDateIdentifier(shippingFee.id);
                const confirmingActualDate = shippingFee?.confirmingActualDate;
                const actualDateInEditMode = shippingFee?.actualDateInEditMode;
                const actualDateConfirmed = shippingFee?.actualDateConfirmed;
                const showConfirmedStateForActualDate = shippingFee?.actualDateConfirmedLocally && !dateEditableAfterUpdate;

                const showInput = (!(shippingFee?.actualDelivery) || actualDateInEditMode);
                const showCopyEstimationToActualValuesButton = showInput && !confirmingActualDate;
                return {
                    [ShippingFeesDataGridColumnNames.estimatedDeliveryDate]:
                        <div className={'d-flex align-items-center justify-content-end'}>
                            <p className={'text-sm'}>
                                {DateUtils.serverToLocal(shippingFee?.estimatedDeliveryDateTime).format(DateUtils.dateFormat)}
                            </p>
                            <Fade in={showCopyEstimationToActualValuesButton}>
                                <IconButton
                                    disabled={!showCopyEstimationToActualValuesButton}
                                    color={'secondary'}
                                    onPressed={() => copyEstimationToActualValues(shippingFee, type)}
                                    className={'ml-2'}
                                >
                                    <ArrowForward className='icon normal'/>
                                </IconButton>
                            </Fade>
                        </div>,
                    [ShippingFeesDataGridColumnNames.actualDeliveryDate]: showConfirmedStateForActualDate
                        ? <>
                            <p className={'mr-2'}>
                                {DateUtils.serverToLocal(shippingFee?.actualDelivery).format(DateUtils.dateFormat)}
                            </p>
                            <div className={classnames('confirmation-container confirmed')}>
                                <SaveIcon className={classnames(`save`, "mr-1")}/>
                                <p>
                                    Confirmed
                                </p>
                            </div>
                        </>
                        : !showInput
                            ? <div className={'d-flex align-items-center justify-content-end'}>
                                <p className={'text-sm'}>
                                    {DateUtils.serverToLocal(shippingFee?.actualDelivery).format(DateUtils.dateFormat)}
                                </p>
                                <IconButton
                                    onPressed={() => setInputInEditMode(shippingFee, true, type)}
                                    className={'ml-2'}
                                    color={'secondary'}
                                >
                                    <SvgIcon className='icon normal' component={EditOutlined}/>
                                </IconButton>
                            </div>
                            : <div className={'d-flex align-items-center justify-content-end'}>
                                <DateInputSection
                                    value={shippingFee?.actualDelivery ? DateUtils.serverToUTC(shippingFee?.actualDelivery) : undefined}
                                    disabled={confirmingActualDate}
                                    actualDateConfirmed={actualDateConfirmed}
                                    save={(value) => confirmInputChanges(shippingFee, value, type)}
                                    resetRequested={() => setInputInEditMode(shippingFee, false, type)}
                                    ref={ref => {
                                        actualDateRefs.current[actualDateFormName] = ref;
                                    }}
                                />
                            </div>,
                }
            }
            default:
                return {};
        }

    }, [confirmInputChanges, copyEstimationToActualValues, dateEditableAfterUpdate, feeEditableAfterUpdate, setInputInEditMode])

    const dataGridRows = useMemo(() => {
        return data?.map(shippingFee => {
            return {
                key: shippingFee?.id,
                data: shippingFee,
                cells: {
                    [ShippingFeesDataGridColumnNames.invoiceNo]: () => (
                        <Button
                            className={'px-0 d-flex justify-content-center align-items-center'}
                            variant={'text'}
                            color={'primary'}
                            component={Link}
                            to={getInvoiceLink(shippingFee?.customerId, shippingFee?.invoiceId)}
                            target={'_blank'}
                        >
                            <p style={{cursor: 'pointer'}}>{shippingFee?.invoiceNo ?? ''}</p>
                            <KeyboardArrowRight/>
                        </Button>
                    ),
                    [ShippingFeesDataGridColumnNames.customer]: shippingFee?.customerInfo?.name ?? shippingFee?.customerName,
                    [ShippingFeesDataGridColumnNames.freightCompany]: shippingFee?.freightCompanyTitle,
                    [ShippingFeesDataGridColumnNames.eligibleForFreeShipping]: shippingFee?.isFree
                        ? <Passed/>
                        : <Failed/>,
                    [ShippingFeesDataGridColumnNames.reason]: shippingFee?.friendlyReason?.replaceAll('\n', ''),
                    ...createEstimationAndActualColumns(inputTypes.date, shippingFee),
                    [ShippingFeesDataGridColumnNames.submittedBy]: shippingFee?.submittedBy,
                    [ShippingFeesDataGridColumnNames.submittedDate]: shippingFee?.submittedDateTime,
                    [ShippingFeesDataGridColumnNames.customerFee]: shippingFee?.customerFee,
                    ...createEstimationAndActualColumns(inputTypes.cost, shippingFee),
                    [ShippingFeesDataGridColumnNames.confirmedBy]: shippingFee?.reviewedBy,
                }
            }
        })
    }, [createEstimationAndActualColumns, data, getInvoiceLink])


    return (
        <>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                columns={dataGridColumns}
                initialLayoutState={{
                    columns: columns,
                }}
                rows={dataGridRows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hideFooterActions
                getExportInfo={exportToCsv}
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
            />
        </>
    );
}


const FeeInputSection = forwardRef(({value: valueProps, save, disabled, actualFeeConfirmed, resetRequested}, ref) => {
    const [value, setValue] = React.useState(valueProps);
    const hasValue = !!value;
    const hasChanged = value !== valueProps;
    const canSubmit = hasValue && hasChanged && Number(value) >= 0;

    /**
     * Exposes the copyValue function to the parent component, allowing it to set the value of the input from outside.
     */
    useImperativeHandle(ref, () => ({
        copyValue: (value) => setValue(value),
    }), []);

    /**
     * Updates the local state of the input value when the prop value changes.
     */
    useEffect(() => {
        setValue(valueProps);
    }, [valueProps])

    return (
        <>
            <TextField
                sx={{width: 110}}
                disabled={disabled}
                type={'number'}
                size={'small'}
                inputProps={{min: 0,}}
                value={value ?? ""}
                onChange={(e) => setValue(e.target.value)}
                onBlur={(e) => {
                    if (!e.target.value || Number.isNaN(Number(e.target.value))) {
                        e.target.value = '';
                    }
                    if (Number(e.target.value) < 0) {
                        e.target.value = '0';
                    }
                    setValue(e.target.value);
                }}
            />
            <SaveOrRollback
                loading={disabled}
                column={false}
                showText={false}
                show={{
                    cancel: actualFeeConfirmed,
                    save: canSubmit,
                }}
                onSave={() => save(value)}
                onRollback={() => {
                    setValue(valueProps);
                    resetRequested();
                }}
            />
        </>
    );
})


const DateInputSection = forwardRef(({value: valueProps, save, disabled, actualDateConfirmed, resetRequested}, ref) => {
    const [value, setValue] = React.useState(valueProps);
    const hasValue = !!value;
    const hasChanged = Utils.dateComparator(value, valueProps) !== 0;
    const canSubmit = hasValue && hasChanged && value.isValid();

    /**
     * Exposes the copyValue function to the parent component, allowing it to set the value of the input from outside.
     */
    useImperativeHandle(ref, () => ({
        copyValue: (value) => setValue(value),
    }), []);

    /**
     * Updates the local state of the input value when the prop value changes.
     */
    useEffect(() => {
        setValue(valueProps);
    }, [valueProps])

    return (
        <>
            <DatePicker
                disabled={disabled}
                disableFuture={false}
                className={'width-fit-content'}
                slotProps={{
                    textField: {
                        InputProps: {
                            style: {
                                width: 155,
                            }
                        },
                        inputProps: {
                            style: {
                                padding: '8.5px 14px',
                                width: 85,
                            },
                        },
                    },
                }}
                value={value}
                onChange={(newValue) => setValue(newValue)}
            />
            <SaveOrRollback
                loading={disabled}
                column={false}
                showText={false}
                show={{
                    cancel: actualDateConfirmed,
                    save: canSubmit,
                }}
                onSave={() => save(value)}
                onRollback={() => {
                    setValue(valueProps);
                    resetRequested();
                }}
            />
        </>
    );
})


export default ShippingFeesListViewTable;
